/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { getMDMentionValues, MinimalEditor } from '@sketchnote/minimal-editor';
import { Avatar } from '@sketchnote/shared-components';
import { PlateProvider, TElement } from '@udecode/plate';
import cn from 'classnames';
import { formatRelative, parseISO } from 'date-fns';
import * as React from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useBoardStore } from 'src/features/boards';
import { useFileAccess } from 'src/features/files/hooks/useFileAccess';
import { useTaskAccess } from 'src/features/files/hooks/useTaskAccess';
import { Member } from 'src/type';
import { v4 as uuid } from 'uuid';

import { useTasksStore } from '../store/useTasksStore';
import { Comment as CommentType } from '../types/tasks';
import { checkForEmptySlateObject, checkForEmptySlateObjectWithMention } from './utils';
const { useRef, useState, useCallback } = React;

export const EditItemComments = ({ members }: { members: any }) => {
  const taskId = useTasksStore((s) => s.currentTaskId);

  const item = useTasksStore(
    useCallback(
      (s) => {
        if (taskId) {
          return s.tasks[taskId];
        }
      },
      [taskId]
    )
  );

  const scrollToElement = useRef<HTMLDivElement | null>(null);

  if (!item) return <></>;
  const [id] = useState(() => uuid());

  return (
    <>
      {item?.comments?.map((comment) => (
        <PlateProvider key={comment.id} id={comment.id}>
          <EditItemCommentBase comment={comment} access={item?.access} id={id} members={members} />
        </PlateProvider>
      ))}

      <div ref={scrollToElement}></div>
    </>
  );
};

const EditItemCommentBase = ({
  comment,
  access,
  id,
  members,
}: {
  comment: CommentType;
  access?: string;
  id: string;
  members?: Member[];
}) => {
  const onUpdateComment = useTasksStore((s) => s.onUpdateComment);

  const boardsStore = useBoardStore();
  const taskId = useTasksStore((s) => s.currentTaskId);
  const onDeleteComment = useTasksStore((s) => s.onDeleteComment);
  const item = useTasksStore(
    useCallback(
      (s) => {
        if (taskId) {
          return s.tasks[taskId];
        }
      },
      [taskId]
    )
  );

  const [isEdit, setIsEdit] = useState(false);
  const [editorValue, setEditorValue] = useState<TElement[]>(comment.data);

  const handleEditorChange = (value) => {
    setEditorValue(value);
  };

  const showComment = React.useMemo(
    () => (
      <MinimalEditor
        editorValue={editorValue}
        id={id}
        editableProps={{
          readOnly: !isEdit,
          className: cn({ 'bg-gray-100 rounded p-12 border': isEdit }),
        }}
        onKeyDownHandler={(e) => {
          if (e.shiftKey === true) {
            handleEditorChange;
            e.stopPropagation();
          } else if (e.key === 'Enter') {
            e.preventDefault();
            handleUpdate(comment);
          }
        }}
        members={members || []}
        onEditorChange={handleEditorChange}
      />
    ),
    [comment.id, editorValue, isEdit]
  );

  const handleUpdate = (comment) => {
    const data = checkForEmptySlateObjectWithMention(editorValue);
    if (data.length > 0) {
      const mdUsers = getMDMentionValues(editorValue);
      if (isEdit && taskId) {
        onUpdateComment({
          comment: editorValue,
          commentId: comment.id,
          text: mdUsers.md,
          tags: mdUsers.values,
          itemId: taskId,
        });
      }
      setIsEdit(!isEdit);
    }
  };

  const { isReadOnly } = useTaskAccess(taskId || '');

  return (
    <div className="mb-24 w-full" key={comment.id}>
      <div className="flex justify-between items-center mt-20">
        <div className="flex mb-8 items-center">
          <div className="p-2 border border-gray-200 rounded-full">
            <Avatar
              name={comment?.member?.firstname || ''}
              size="md"
              type="photo"
              src={comment.member?.avatar}
              wrapperClassName="border"
            />
          </div>
          <div className="flex-row">
            <label className="text-sm text-gray-999 ml-12 font-medium ">
              {comment?.member?.firstname}
            </label>
            <p className="text-xss text-gray-500 ml-12 mt-2">
              {comment?.createdAt && formatRelative(parseISO(comment.createdAt), new Date())}
            </p>
          </div>
        </div>
      </div>
      <div className="text-gray-999 text-sm font-regular">
        <DndProvider backend={HTML5Backend}>{showComment}</DndProvider>
      </div>
      {!isReadOnly && (
        <div>
          <button
            disabled={item?.isReadOnly || access === 'view'}
            className="text-xs text-gray-500 mt-4"
            onClick={() => handleUpdate(comment)}
          >
            {isEdit ? 'Save' : 'Edit'}
          </button>
          <span className="text-gray-500">&nbsp;.&nbsp;</span>
          <button
            disabled={item?.isReadOnly || access === 'view' || boardsStore.isPublicView}
            className="text-xs text-gray-500 mt-4"
            onClick={() => {
              if (taskId) {
                onDeleteComment({
                  commentId: comment.id,
                  itemId: taskId,
                });
              }
            }}
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
};
